import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { useAnmeldung } from "../zustand/Anmeldung";
import { useDemo } from "../zustand/DemoZustand";
import useDokumentSitzung from "../haken/useDokumentSitzung";
import Zellraster from "../bausteine/Zellraster";
import Eingabezeile from "../bausteine/Eingabezeile";
import Tabellenleiste from "../bausteine/Tabellenleiste";
import Rastermenue from "../bausteine/Rastermenue";
import Tabellendruck from "../bausteine/Tabellendruck";
import Randnotizen from "../bausteine/Randnotizen";
import Notizfeld from "../bausteine/Notizfeld";
import { farbeFuer } from "../bausteine/Textwerk";
import { PfeilRechts, Warnung } from "../bausteine/Symbole";
import {
  alsGitter,
  ersetzeBlatt,
  fuegeSpalteEin,
  fuegeZeileEin,
  liesZelle,
  loescheSpalte,
  loescheZeile,
  schreibeZelle,
  setzeSpaltenbreite,
  setzeSpaltenformat,
  spaltenVon,
  stelleGeruestSicher,
  verschiebeSpalte,
  verschiebeZeile,
  zeilenVon,
  zellenVon,
} from "../hilfen/rechenblatt";
import { rechneDurch } from "../hilfen/tabellenrechnung";
import { alsAnzeige, fehlertext, istFehler } from "../hilfen/formelrechner";
import { versetzeFormel } from "../hilfen/formelverschieber";
import { formatiere } from "../hilfen/zahlformat";
import { spaltenname, zellschluessel } from "../hilfen/zellbezug";
import { ausCsv, biete, nachCsv } from "../hilfen/tabellencsv";
import { benenneDokument } from "../dienst/server";

/*
  Eine Tabelle, offen.

  Oben die Werkzeugleiste und die Eingabezeile, in der Mitte das Blatt,
  rechts die Kommentare. Alles, was mit dem Netz zu tun hat, steckt in
  `useDokumentSitzung` — dieselbe Maschinerie wie beim Textdokument und bei
  der Präsentation, denn eine Tabelle IST ein Dokument, nur mit anderem
  Inhalt.
*/

export default function Tabelle({ dokumentId, oeffne }) {
  const { token, konto, fraktion } = useAnmeldung();
  const { zustand, ich, aktionen } = useDemo();

  const {
    doc,
    satz,
    setzeSatz,
    laedt,
    fehler,
    setzeFehler,
    bereit,
    andere,
    meldeMarke,
  } = useDokumentSitzung(dokumentId);

  const [titel, setzeTitel] = useState("");
  const [gewaehlt, setzeGewaehlt] = useState({ spalte: 0, zeile: 0 });
  const [bearbeitung, setzeBearbeitung] = useState(null);
  const [menue, setzeMenue] = useState(null);
  const [kopie, setzeKopie] = useState(null);
  const [druckt, setzeDruckt] = useState(false);
  const [notizEntwurf, setzeNotizEntwurf] = useState(null);
  const [notizBearbeiten, setzeNotizBearbeiten] = useState(null);
  const [rueckgaengig, setzeRueckgaengig] = useState(null);
  const [kannZurueck, setzeKannZurueck] = useState(false);
  const [kannVor, setzeKannVor] = useState(false);
  /* Zwingt ein Neurechnen, wenn sich im Blatt etwas ändert. */
  const [takt, setzeTakt] = useState(0);

  const csvWaehler = useRef(null);

  useEffect(() => {
    if (satz) setzeTitel(satz.titel);
  }, [satz]);

  /*
    Der Horcher muss VOR dem Gerüst hängen.

    React führt die Wirkungen in der Reihenfolge aus, in der sie dastehen.
    Stünde das Anlegen des Gerüsts zuerst, schriebe es ins Dokument, bevor
    irgendjemand zuhört — der Takt bliebe stehen, die Berechnung liefe nie
    neu, und das Blatt bliebe bei „0 Spalten · 0 Zeilen" stehen. Genau so
    ist es beim ersten Versuch passiert.
  */
  useEffect(() => {
    if (!bereit) return undefined;
    const beiAenderung = () => setzeTakt((n) => n + 1);
    doc.on("update", beiAenderung);
    return () => doc.off("update", beiAenderung);
  }, [bereit, doc]);

  /*
    Das Startgerüst legt NUR an, wer die Tabelle angelegt hat — und erst,
    wenn die Sitzung bereit ist. Beides teuer gelernt: Vor „bereit" ginge es
    nicht hinaus, und legten zwei Leute gleichzeitig an, stünden zwölf
    Spalten statt sechs da.
  */
  useEffect(() => {
    if (!bereit || !satz) return;
    if (satz.konto !== konto.id) return;
    stelleGeruestSicher(doc);
    setzeTakt((n) => n + 1);
  }, [bereit, satz, doc, konto]);

  /*
    Rückgängig über das ganze Blatt.

    Der Verwalter umfasst alle drei Wurzeln — Spalten, Zeilen, Zellen —,
    denn eine eingefügte Zeile ändert zwei davon auf einmal, und beides
    gehört in denselben Schritt zurück.

    `trackedOrigins: new Set([null])` ist die entscheidende Zeile:
    Zurückgenommen wird ausschließlich, was aus DIESEM Browser kam. Was über
    das Netz hereinkommt, trägt „server" und bleibt unberührt.
  */
  useEffect(() => {
    if (!bereit) return undefined;

    const verwalter = new Y.UndoManager(
      [spaltenVon(doc), zeilenVon(doc), zellenVon(doc)],
      { trackedOrigins: new Set([null]), captureTimeout: 400 }
    );
    setzeRueckgaengig(verwalter);

    const standMelden = () => {
      setzeKannZurueck(verwalter.canUndo());
      setzeKannVor(verwalter.canRedo());
    };
    verwalter.on("stack-item-added", standMelden);
    verwalter.on("stack-item-popped", standMelden);
    verwalter.on("stack-cleared", standMelden);

    const beiTaste = (e) => {
      const befehl = e.metaKey || e.ctrlKey;
      if (!befehl || e.key.toLowerCase() !== "z") return;
      e.preventDefault();
      if (e.shiftKey) verwalter.redo();
      else verwalter.undo();
    };
    document.addEventListener("keydown", beiTaste);

    return () => {
      document.removeEventListener("keydown", beiTaste);
      verwalter.destroy();
      setzeRueckgaengig(null);
    };
  }, [bereit, doc]);

  /*
    Rechnen: das ganze Blatt bei jeder Änderung.

    Das Ergebnis liegt NICHT in Yjs — es ist abgeleitet. Es dort abzulegen
    hieße doppelten Verkehr und Streit zwischen Geräten, die zu
    verschiedenen Zeitpunkten gerechnet haben.
  */
  const { gitter, werte, ringe } = useMemo(() => {
    if (!bereit) {
      return {
        gitter: { spalten: [], zeilen: [], zellen: new Map() },
        werte: new Map(),
        ringe: new Set(),
      };
    }
    const jetzt = alsGitter(doc);
    const gerechnet = rechneDurch(jetzt);
    return { gitter: jetzt, werte: gerechnet.werte, ringe: gerechnet.ringe };
    /* `takt` zwingt das Neurechnen nach jeder Änderung. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bereit, doc, takt]);

  /* Die Wahl darf nie hinter das Blatt zeigen — Zeilen können verschwinden. */
  const stelle = {
    spalte: Math.max(0, Math.min(gewaehlt.spalte, gitter.spalten.length - 1)),
    zeile: Math.max(0, Math.min(gewaehlt.zeile, gitter.zeilen.length - 1)),
  };

  const spalteJetzt = gitter.spalten[stelle.spalte];
  const zeileJetzt = gitter.zeilen[stelle.zeile];
  const schluesselJetzt =
    spalteJetzt && zeileJetzt ? zellschluessel(spalteJetzt.id, zeileJetzt.id) : "";

  const bezug =
    gitter.spalten.length > 0 ? `${spaltenname(stelle.spalte)}${stelle.zeile + 1}` : "";

  const rohWert =
    bereit && spalteJetzt && zeileJetzt
      ? liesZelle(doc, spalteJetzt.id, zeileJetzt.id)
      : "";

  /*
    Wo die anderen stehen.

    `meldeMarke` ist auf Textstellen gemünzt und erwartet zwei Zahlen. Hier
    werden sie UMGEDEUTET: `von` ist die Spalte, `bis` die Zeile. Das spart
    einen Eingriff am Server und ist ein bewusster Kniff — wer diese Zeilen
    später liest, soll ihn nicht für einen Textversatz halten.

    Der Gewinn ist echt: Weil in derselben Zelle der letzte Schreiber
    gewinnt, ist „Renate steht in C7" genau die Vorwarnung, die es braucht.
  */
  const fremde = useMemo(
    () =>
      andere
        .filter((p) => Number.isFinite(p.von) && Number.isFinite(p.bis))
        .map((p) => ({
          kontoId: p.kontoId,
          name: p.name,
          farbe: p.farbe || farbeFuer(p.kontoId),
          spalte: p.von,
          zeile: p.bis,
        })),
    [andere]
  );

  const merkeStelle = useCallback(
    (neu) => {
      setzeGewaehlt(neu);
      meldeMarke({ von: neu.spalte, bis: neu.zeile });
    },
    [meldeMarke]
  );

  /* ---------- Zellen schreiben ---------- */

  /*
    Eine Zelle öffnen — mit oder ohne erstes Zeichen.

    Ist schon eine offen, wird ANGEHÄNGT statt ersetzt: Wer schnell tippt,
    erzeugt Tastendrücke dichter, als React neu zeichnet. Ohne das bliebe
    von „Position" nur das letzte Zeichen übrig.

    `alleMarkieren` entscheidet, was beim Öffnen markiert ist. Über Enter
    oder F2 geöffnet: alles, damit Tippen es ersetzt. Beim Lostippen: nichts,
    sonst überschriebe das zweite Zeichen das erste.
  */
  function oeffneZelle(ersteZeichen) {
    if (!spalteJetzt || !zeileJetzt) return;
    setzeBearbeitung((bisher) => {
      if (bisher) {
        return ersteZeichen === undefined
          ? bisher
          : { ...bisher, text: bisher.text + ersteZeichen };
      }
      return ersteZeichen === undefined
        ? { text: rohWert, alleMarkieren: true }
        : { text: ersteZeichen, alleMarkieren: false };
    });
  }

  function schreibeUndGeh(text, richtung) {
    if (!spalteJetzt || !zeileJetzt) return;

    /* Nur schreiben, wenn sich etwas geändert hat — sonst erzeugte jedes
       bloße Anklicken einer Zelle ein Netzpaket. */
    if ((text ?? "") !== rohWert) {
      schreibeZelle(doc, spalteJetzt.id, zeileJetzt.id, text ?? "");
    }
    setzeBearbeitung(null);

    const schritte = {
      runter: [0, 1],
      hoch: [0, -1],
      rechts: [1, 0],
      links: [-1, 0],
      bleiben: [0, 0],
    };
    const [dSpalte, dZeile] = schritte[richtung] || schritte.bleiben;
    if (dSpalte || dZeile) {
      merkeStelle({
        spalte: Math.max(0, Math.min(stelle.spalte + dSpalte, gitter.spalten.length - 1)),
        zeile: Math.max(0, Math.min(stelle.zeile + dZeile, gitter.zeilen.length - 1)),
      });
    }
  }

  /* ---------- Kopieren ---------- */

  /*
    Kopiert wird der TEXT der Zelle, samt Formel — und die Stelle, an der er
    stand. Beim Einsetzen wandern die Bezüge um die Strecke dazwischen mit.

    Hier — und nur hier — wirkt das Dollarzeichen: `=B2/B$6`, eine Zeile
    tiefer eingesetzt, wird zu `=B3/B$6`. Das B2 wandert, die Summe in Zeile
    6 bleibt stehen.
  */
  function kopiere() {
    if (!spalteJetzt || !zeileJetzt) return;
    setzeKopie({ text: rohWert, spalte: stelle.spalte, zeile: stelle.zeile });
  }

  function setzeEin() {
    if (!kopie || !spalteJetzt || !zeileJetzt) return;
    const versetzt = versetzeFormel(
      kopie.text,
      stelle.spalte - kopie.spalte,
      stelle.zeile - kopie.zeile
    );
    schreibeZelle(doc, spalteJetzt.id, zeileJetzt.id, versetzt);
  }

  /*
    Strg+C und Strg+V — aber nicht, während eine Zelle offen ist.

    Dort gehören die beiden dem Text: Wer eine lange Formel bearbeitet,
    will einen Ausschnitt daraus kopieren, nicht die ganze Zelle.

    Die Wirkung hat bewusst KEINE Abhängigkeitsliste. `kopiere` und
    `setzeEin` entstehen bei jedem Zeichnen neu und lesen die aktuelle
    Stelle; mit einer Liste hinge der Horcher an einer veralteten Fassung
    und setzte in die falsche Zelle ein.
  */
  useEffect(() => {
    if (!bereit || bearbeitung) return undefined;
    const beiTaste = (e) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const taste = e.key.toLowerCase();
      if (taste === "c") {
        e.preventDefault();
        kopiere();
      } else if (taste === "v") {
        e.preventDefault();
        setzeEin();
      }
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  });

  /* ---------- Kommentare ---------- */

  /*
    Kommentare hängen an der ZELLE, nicht an einem Zeichen darin — so macht
    es jede Tabellenkalkulation. In `notizen` nimmt `datei` die
    Dokumentkennung und `absatz` den Zellschlüssel; das Feld ist freier
    Text, es braucht also keine neue Spalte.

    Eine Verankerung im Text gibt es hier nicht: Der Kommentar zeigt auf die
    Zelle, und die bleibt dieselbe, auch wenn jemand ihren Inhalt ändert.
  */
  const markierungen = useMemo(() => {
    if (!bereit) return [];
    const vorhanden = new Set(
      gitter.spalten.flatMap((s) => gitter.zeilen.map((z) => zellschluessel(s.id, z.id)))
    );
    return (zustand.annotationen || [])
      .filter(
        (a) =>
          a.dateiId === dokumentId &&
          (a.sichtbarkeit === "fraktion" || a.verfasserId === ich.id) &&
          a.notiz &&
          a.notiz.trim() &&
          String(a.absatzId || "").includes("|")
      )
      .map((a) => ({
        ...a,
        /*
          `stelle` trägt hier keine Textposition, sondern nur die Auskunft
          „die Zelle gibt es noch". `Randnotizen` zeigt eine Notiz ohne
          `stelle` als verwaist — und verwaist ist sie genau dann, wenn die
          Zeile oder Spalte gelöscht wurde.
        */
        stelle: vorhanden.has(a.absatzId) ? { start: 0, ende: 0 } : null,
      }));
  }, [bereit, zustand.annotationen, dokumentId, ich.id, gitter]);

  async function notizSichern(text) {
    const dazu = notizEntwurf;
    if (!dazu) return;
    try {
      await aktionen.fuegeAnnotationHinzu({
        dateiId: dokumentId,
        absatzId: dazu.schluessel,
        start: 0,
        ende: 0,
        /*
          `notizen.zitat` ist ein Pflichtfeld. Bei einer leeren Zelle
          springt der Bezug ein — sonst antwortet der Server mit 400, und
          zwar ausgerechnet dann, wenn jemand eine noch leere Zelle
          kommentieren will („hier fehlt noch der Ansatz").
        */
        zitat: dazu.zitat || dazu.bezug,
        notiz: text,
        sichtbarkeit: "fraktion",
        farbe: "gelb",
        verankerung: "",
      });
      setzeNotizEntwurf(null);
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  /* Vom Kommentar am Rand zurück in die Zelle. */
  function springeZu(notiz) {
    for (let s = 0; s < gitter.spalten.length; s += 1) {
      for (let z = 0; z < gitter.zeilen.length; z += 1) {
        if (zellschluessel(gitter.spalten[s].id, gitter.zeilen[z].id) === notiz.absatzId) {
          merkeStelle({ spalte: s, zeile: z });
          return;
        }
      }
    }
  }

  function beginneKommentar() {
    if (!spalteJetzt || !zeileJetzt) return;
    const wert = werte.get(schluesselJetzt);
    const anzeige = alsAnzeige(wert);
    const inhalt =
      anzeige.zahl !== null ? formatiere(anzeige.zahl, spalteJetzt.format) : anzeige.text;
    /*
      Bei einer leeren Zelle springt der Bezug als Zitat ein — schon HIER,
      nicht erst beim Sichern. Sonst stünde im Dialog ein leeres
      Anführungszeichenpaar „“, und das sieht aus wie ein Fehler statt wie
      eine leere Zelle. (Nebenbei ist `notizen.zitat` ein Pflichtfeld: Ohne
      Ersatz antwortete der Server mit 400.)
    */
    setzeNotizEntwurf({ schluessel: schluesselJetzt, bezug, zitat: inhalt || bezug });
  }

  /* ---------- Zeilen und Spalten ---------- */

  function menueFuer(art, nr, knopf) {
    const r = knopf.getBoundingClientRect();
    setzeMenue({
      art,
      stelle: nr,
      anker: { links: r.left, oben: r.top, unten: r.bottom },
    });
  }

  const menueSchliessen = useCallback(() => setzeMenue(null), []);

  /* ---------- Drucken ---------- */

  useEffect(() => {
    if (!druckt) return undefined;
    /* Erst zeichnen lassen, dann drucken — sonst ginge der Stapel leer in
       den Drucker, weil React ihn noch gar nicht gezeichnet hätte. */
    const uhr = setTimeout(() => window.print(), 400);
    const fertig = () => setzeDruckt(false);
    window.addEventListener("afterprint", fertig);
    return () => {
      clearTimeout(uhr);
      window.removeEventListener("afterprint", fertig);
    };
  }, [druckt]);

  /* ---------- CSV ---------- */

  function csvHinaus() {
    const name = `${(satz ? satz.titel : "Tabelle").replace(/[^\wäöüÄÖÜß -]/g, "")}.csv`;
    biete(name, nachCsv(gitter, werte));
  }

  async function csvHerein(datei) {
    if (!datei) return;
    setzeFehler("");
    try {
      const text = await datei.text();
      const { reihen, formate, zuVieleZeilen, zuVieleSpalten } = ausCsv(text);
      if (reihen.length === 0) {
        setzeFehler("In dieser Datei stand nichts, was sich als Tabelle lesen ließe.");
        return;
      }
      if (
        !window.confirm(
          `„${datei.name}" einlesen? Das ersetzt das GANZE Blatt — ${reihen.length} Zeilen, ${formate.length} Spalten. Mit Strg+Z lässt es sich zurücknehmen.`
        )
      )
        return;

      ersetzeBlatt(doc, reihen, formate);
      merkeStelle({ spalte: 0, zeile: 0 });

      if (zuVieleZeilen || zuVieleSpalten) {
        setzeFehler(
          "Die Datei war größer als das, was hier hineinpasst — sie wurde auf 200 Zeilen und 50 Spalten gekürzt."
        );
      }
    } catch (f) {
      setzeFehler(`Die Datei ließ sich nicht lesen: ${f.message}`);
    }
  }

  /* ---------- Titel ---------- */

  async function sichereTitel() {
    const neu = titel.trim();
    if (!neu || !satz || neu === satz.titel) {
      setzeTitel(satz ? satz.titel : "");
      return;
    }
    try {
      setzeSatz(await benenneDokument(token, dokumentId, neu));
    } catch (f) {
      setzeFehler(f.message);
      setzeTitel(satz.titel);
    }
  }

  /*
    Die Statuszeile.

    In der Zelle ist für `#DIV/0!` gerade Platz, für eine Erklärung nicht.
    Wer zweimal im Jahr eine Tabelle anfasst, kann mit dem kurzen Zeichen
    allein nichts anfangen — der ganze Satz steht deshalb hier.
  */
  const hinweis = useMemo(() => {
    if (!bereit) return "";
    const wert = werte.get(schluesselJetzt);
    if (istFehler(wert)) return `${wert.code} — ${fehlertext(wert.code)}`;
    if (ringe.size > 0) {
      return `Ringschluss in ${ringe.size} Zelle${ringe.size === 1 ? "" : "n"} — eine Formel rechnet mit sich selbst.`;
    }
    return "";
  }, [bereit, werte, ringe, schluesselJetzt]);

  if (laedt) {
    return <p className="leise" style={{ marginTop: "var(--luft-5)" }}>Wird geöffnet …</p>;
  }

  if (fehler && !satz) {
    return (
      <div className="karte" style={{ marginTop: "var(--luft-5)" }}>
        <p className="tor__fehler">
          <Warnung groesse={18} />
          <span>{fehler}</span>
        </p>
        <button
          className="knopf knopf--zurueckhaltend"
          style={{ marginTop: "var(--luft-4)" }}
          onClick={() => oeffne("office", {})}
        >
          Zurück zu Office
        </button>
      </div>
    );
  }

  const menueSpalte = menue && menue.art === "spalte" ? gitter.spalten[menue.stelle] : null;

  return (
    <>
      <div className="detailkopf nicht-drucken">
        <p className="detailkopf__vorspann">
          {fraktion ? `${fraktion.name} · ${fraktion.kommune}` : "Fraktion"}
        </p>

        <div className="dokumentkopf">
          <input
            className="dokumenttitel"
            value={titel}
            onChange={(e) => setzeTitel(e.target.value)}
            onBlur={sichereTitel}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            aria-label="Titel der Tabelle"
          />

          <div className="dokumentkopf__leute">
            {andere.map((p) => (
              <span
                key={p.kontoId}
                className="leutekreis"
                style={{ background: p.farbe || farbeFuer(p.kontoId) }}
                title={`${p.name} ist in der Tabelle`}
              >
                {kuerzel(p.name)}
              </span>
            ))}
          </div>
        </div>

        {fehler && (
          <p className="klein" style={{ color: "var(--ampel-nein)" }} role="alert">
            {fehler}
          </p>
        )}
      </div>

      <Tabellenleiste
        bezug={bezug}
        kannZurueck={kannZurueck}
        kannVor={kannVor}
        rueckgaengig={rueckgaengig}
        hatKopie={!!kopie}
        aufZeileEinfuegen={() => fuegeZeileEin(doc, stelle.zeile)}
        aufSpalteEinfuegen={() => fuegeSpalteEin(doc, stelle.spalte)}
        aufKopieren={kopiere}
        aufEinsetzen={setzeEin}
        aufKommentar={beginneKommentar}
        kannKommentieren={bereit && !!spalteJetzt}
        aufDrucken={() => setzeDruckt(true)}
        aufCsvAus={csvHinaus}
        aufCsvEin={() => csvWaehler.current && csvWaehler.current.click()}
      />

      <input
        ref={csvWaehler}
        type="file"
        accept=".csv,text/csv,text/plain"
        style={{ display: "none" }}
        onChange={(e) => {
          const datei = e.target.files && e.target.files[0];
          e.target.value = "";
          csvHerein(datei);
        }}
      />

      <Eingabezeile
        bezug={bezug}
        wert={bearbeitung ? bearbeitung.text : rohWert}
        aus={!bereit || !!bearbeitung}
        aufFertig={schreibeUndGeh}
        aufAbbruch={() => setzeBearbeitung(null)}
      />

      <div className="rechenblattsaal">
        <div className="rechenblatt">
          {bereit && gitter.spalten.length > 0 ? (
            <Zellraster
              gitter={gitter}
              werte={werte}
              gewaehlt={stelle}
              aufWahl={merkeStelle}
              bearbeitung={bearbeitung}
              aufOeffnen={oeffneZelle}
              aufAendern={(text) => setzeBearbeitung((b) => ({ ...b, text }))}
              aufFertig={schreibeUndGeh}
              aufAbbruch={() => setzeBearbeitung(null)}
              aufKopf={(nr, knopf) => menueFuer("spalte", nr, knopf)}
              aufZeilenkopf={(nr, knopf) => menueFuer("zeile", nr, knopf)}
              fremde={fremde}
            />
          ) : (
            <p className="leise" style={{ padding: "var(--luft-4)" }}>
              Das Blatt wird vorbereitet …
            </p>
          )}

          <p className="klein rechenblatt__fuss nicht-drucken">
            {hinweis ? (
              <span className="rechenblatt__hinweis">{hinweis}</span>
            ) : (
              <span className="leise">
                {gitter.spalten.length} Spalten · {gitter.zeilen.length} Zeilen
              </span>
            )}
            {" · "}
            <button
              className="knopf knopf--schlicht"
              onClick={() => oeffne("office", {})}
              style={{ padding: 0 }}
            >
              Zurück zu Office
              <PfeilRechts groesse={14} />
            </button>
          </p>
        </div>

        <Randnotizen
          markierungen={markierungen}
          aufSpringen={springeZu}
          aufBearbeiten={setzeNotizBearbeiten}
          verwaistText="Die Zelle dazu gibt es nicht mehr — Zeile oder Spalte wurde gelöscht."
          leerText="Noch keine Kommentare. Wähl eine Zelle und drück „Kommentar“ — etwa dort, wo noch eine Zahl fehlt."
        />
      </div>

      {menue && (
        <Rastermenue
          art={menue.art}
          stelle={menue.stelle}
          name={menue.art === "spalte" ? spaltenname(menue.stelle) : menue.stelle + 1}
          format={menueSpalte ? menueSpalte.format : undefined}
          breite={menueSpalte ? menueSpalte.breite : undefined}
          anzahl={menue.art === "spalte" ? gitter.spalten.length : gitter.zeilen.length}
          anker={menue.anker}
          aufEinfuegenDavor={() => {
            if (menue.art === "spalte") fuegeSpalteEin(doc, menue.stelle);
            else fuegeZeileEin(doc, menue.stelle);
            menueSchliessen();
          }}
          aufEinfuegenDanach={() => {
            if (menue.art === "spalte") fuegeSpalteEin(doc, menue.stelle + 1);
            else fuegeZeileEin(doc, menue.stelle + 1);
            menueSchliessen();
          }}
          aufLoeschen={() => {
            if (menue.art === "spalte") loescheSpalte(doc, menue.stelle);
            else loescheZeile(doc, menue.stelle);
            menueSchliessen();
          }}
          aufHoch={() => {
            if (menue.art === "spalte") verschiebeSpalte(doc, menue.stelle, menue.stelle - 1);
            else verschiebeZeile(doc, menue.stelle, menue.stelle - 1);
            menueSchliessen();
          }}
          aufRunter={() => {
            if (menue.art === "spalte") verschiebeSpalte(doc, menue.stelle, menue.stelle + 1);
            else verschiebeZeile(doc, menue.stelle, menue.stelle + 1);
            menueSchliessen();
          }}
          aufFormat={(format) => {
            setzeSpaltenformat(doc, menue.stelle, format);
            menueSchliessen();
          }}
          aufBreite={(breite) => {
            setzeSpaltenbreite(doc, menue.stelle, breite);
            menueSchliessen();
          }}
          aufSchliessen={menueSchliessen}
        />
      )}

      {notizEntwurf && (
        <Notizfeld
          zitat={notizEntwurf.zitat}
          aufSichern={notizSichern}
          aufAbbrechen={() => setzeNotizEntwurf(null)}
        />
      )}

      {notizBearbeiten && (
        <Notizfeld
          zitat={notizBearbeiten.zitat}
          wert={notizBearbeiten.notiz}
          aufSichern={async (text) => {
            await aktionen.setzeAnnotationNotiz(notizBearbeiten.id, text);
            setzeNotizBearbeiten(null);
          }}
          aufEntfernen={async () => {
            await aktionen.entferneAnnotation(notizBearbeiten.id);
            setzeNotizBearbeiten(null);
          }}
          aufAbbrechen={() => setzeNotizBearbeiten(null)}
        />
      )}

      {druckt && (
        <Tabellendruck gitter={gitter} werte={werte} titel={satz ? satz.titel : "Tabelle"} />
      )}
    </>
  );
}

/* „Renate Buschmann“ → „RB“ */
function kuerzel(name) {
  return String(name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join("");
}
